import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  deleteUserRate,
  editUserRates,
  getCalculationTypes,
  getUserRates,
  saveUserRates,
} from '../../../../../../packages/common/api';
import ChevronIcon from '../../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import PlusIcon from '../../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import ReportDeleteIcon from '../../../../../../packages/common/shared-ui/src/icons/ReportDeleteIcon';
import SubmitIcon from '../../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import UserNewEditIcon from '../../../../../../packages/common/shared-ui/src/icons/UserNewEditIcon';
import { useTranslation } from '../../../../../../packages/common/utils/getLocalString';
import { LanguageMap } from '../../../../../../packages/common/utils/languageMap';

// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: rowButton
const StyledRowButton = styled(IconButton)(theme => ({
  '&.MuiButtonBase-root.MuiIconButton-root': {
    borderLeft: '1px solid #E4E4EF',
    height: '100%',
    borderRadius: 0,
    minWidth: '32px',
  },

  '& .MuiSvgIcon-root': {
    height: 'auto',
  },
}));
// Original class: rowItem
const StyledRowItem = styled(Grid)(theme => ({
  borderTop: '1px solid #E4E4EF',
}));
// Original class: rowCell
const StyledRowCell = styled(Grid)(theme => ({
  padding: '10px',
  fontSize: '14px',
  flex: 1,

  '& + .MuiGrid-root': {
    borderLeft: '1px solid #E4E4EF',
  },

  '& .MuiFormControl-root': {
    height: '100%',
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '-3px',
    transform: 'none',
    left: '10px',
    top: '15px',
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select)(theme => ({
  '&.MuiInputBase-root.MuiInput-root': {
    marginTop: 0,
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '10px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
  },
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
// Original class: rowInput
const StyledRowInput = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      padding: 0,
      '&::placeholder': {
        opacity: 1,
      },
    },
  },
}));
// Original class: datePicker
const StyledDatePicker = styled(DatePicker)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: '100%',
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      padding: 0,
      color: '#7174AC',
      fontSize: '14px',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
// Original class: rowHeader
const StyledRowHeader = styled(Grid)(theme => ({
  padding: '18px 16px',
  fontWeight: 'bold',
  flex: 1,

  '& + .MuiGrid-root': {
    borderLeft: '1px solid #E4E4EF',
  },
}));

const UserPageRatesTab = () => {
  const getLocalString = useTranslation();
  const [userRates, setUserRates] = useState([]);
  const [types, setTypes] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const pageParams = useParams();
  const [language, setLanguage] = useState('ru');

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);

  useEffect(() => {
    setLoading(true);
    const getTypes = getCalculationTypes();
    const getRates = getUserRates(pageParams.id);
    function handleResults(results) {
      const errors = results
        .filter(result => result.status === 'rejected')
        .map(result => result.reason);
      if (errors.length) {
        console.error(errors, 'ERROR IN GET');
      }
      return results.map(result => result.value);
    }
    Promise.allSettled([getTypes, getRates]).then(data => {
      const [type, rate] = handleResults(data);
      setTypes(type.data);
      setUserRates(rate.data);
      setLoading(false);
    });
  }, [pageParams.id]);

  const checkRowValue = useCallback(
    value => {
      if (value.dateFrom === 'Invalid Date') {
        value = { ...value, dateFrom: '' };
      }
      const uniqueValue = userRates.filter(
        item =>
          item.xlanceLiteralsId === value.xlanceLiteralsId &&
          dayjs(item.dateFrom).format('DD.MM.YYYY') ===
            dayjs(value.dateFrom).format('DD.MM.YYYY'),
      );
      if (uniqueValue.length > 1) {
        setError(true);
        return false;
      }
      return value.dateFrom && value.rate;
    },
    [userRates],
  );

  const addObjectHandler = useCallback(() => {
    setUserRates([
      ...userRates,
      { new: true, xlanceLiteralsId: '', rate: 0, dateFrom: dayjs(new Date()) },
    ]);
  }, [userRates]);

  const selectHandler = useCallback(
    (event, index) => {
      const updatedObject = {
        ...userRates[index],
        xlanceLiteralsId: event.target.value,
      };
      setUserRates([
        ...userRates.slice(0, index),
        updatedObject,
        ...userRates.slice(index + 1),
      ]);
    },
    [userRates],
  );
  const dateHandler = useCallback(
    (value, index) => {
      const updatedObject = {
        ...userRates[index],
        dateFrom: dayjs(value.$d),
      };
      setUserRates([
        ...userRates.slice(0, index),
        updatedObject,
        ...userRates.slice(index + 1),
      ]);
    },
    [userRates],
  );
  const handleTextField = useCallback(
    (event, index) => {
      let formattedValue;
      const reg = new RegExp(/\D/g);
      if (reg.test(event.target.value)) {
        formattedValue = 0;
      } else {
        formattedValue = Math.round(event.target.value);
      }
      const updatedObject = { ...userRates[index], rate: formattedValue };
      setUserRates([
        ...userRates.slice(0, index),
        updatedObject,
        ...userRates.slice(index + 1),
      ]);
    },
    [userRates],
  );
  const toggleEdit = useCallback(
    index => {
      const updatedObject = { ...userRates[index], edit: true };
      setUserRates([
        ...userRates.slice(0, index),
        updatedObject,
        ...userRates.slice(index + 1),
      ]);
    },
    [userRates],
  );
  const saveEditHandler = useCallback(
    async index => {
      const checkedValue = checkRowValue(userRates[index]);
      if (checkedValue) {
        const data = {
          xlanceLiteralsId:
            userRates[index].xlanceLiteralsId === ''
              ? null
              : userRates[index].xlanceLiteralsId,
          rate: userRates[index].rate,
          dateFrom: userRates[index].dateFrom,
        };
        try {
          let response;
          let updatedObject = { ...userRates[index] };
          if (userRates[index].new) {
            response = await saveUserRates(pageParams.id, data);
            if (response) {
              updatedObject = { ...userRates[index], id: response.data.id };
              delete updatedObject.new;
            }
          }
          if (userRates[index].edit) {
            const id = userRates[index].id;
            response = await editUserRates(pageParams.id, id, data);
            response && delete updatedObject.edit;
          }
          if (response) {
            setError(false);
            setUserRates([
              ...userRates.slice(0, index),
              updatedObject,
              ...userRates.slice(index + 1),
            ]);
          }
        } catch (error) {
          console.error('ERROR WITH SAVE', error);
        }
      }
    },
    [checkRowValue, pageParams.id, userRates],
  );
  const deleteHandler = useCallback(
    async index => {
      if (userRates[index].new) {
        const newArray = userRates.filter((value, i) => i !== index);
        setUserRates(newArray);
        return;
      }
      try {
        const response = await deleteUserRate(
          pageParams.id,
          userRates[index].id,
        );
        if (response) {
          const newArray = userRates.filter((value, i) => i !== index);
          setUserRates(newArray);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [pageParams.id, userRates],
  );
  const buttonsMap = new Map([
    [
      'edit',
      {
        handler: toggleEdit,
        icon: <UserNewEditIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'submit',
      {
        handler: saveEditHandler,
        icon: <SubmitIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
    [
      'delete',
      {
        handler: deleteHandler,
        icon: <ReportDeleteIcon width='16' height='16' viewBox='0 0 16 16' />,
      },
    ],
  ]);

  const RowButton = ({ children, action, rowIndex }) => {
    children = buttonsMap.get(action).icon;
    return (
      <StyledRowButton onClick={() => buttonsMap.get(action).handler(rowIndex)}>
        {children}
      </StyledRowButton>
    );
  };

  return (
    <>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <>
          {error && (
            <Typography
              sx={{
                padding: '4px 0',
                lineHeight: '20px',
                fontSize: 16,
                fontWeight: 600,
                color: '#E03737',
              }}
            >
              {getLocalString('AdminUsers', 'errors', 'rate')}
            </Typography>
          )}
          <Grid
            container
            sx={{
              marginTop: '20px',
            }}
          >
            <Grid sx={{ maxWidth: '630px', flex: 1 }}>
              <StyledBlockWrapper
                sx={{
                  p: 0,
                }}
              >
                <Grid container>
                  <StyledRowHeader sx={{ minWidth: '150px' }}>
                    <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                      {`${getLocalString('AdminUsers', 'labels', 'calculationType')} `}
                    </Typography>
                  </StyledRowHeader>
                  <StyledRowHeader sx={{ minWidth: '150px' }}>
                    <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                      {getLocalString('AdminUsers', 'labels', 'rate')}
                    </Typography>
                  </StyledRowHeader>
                  <StyledRowHeader sx={{ flexShrink: 0, flexBasis: '226px' }}>
                    <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                      {`${getLocalString('AdminUsers', 'labels', 'date')} `}
                    </Typography>
                  </StyledRowHeader>
                </Grid>
                {userRates.length > 0 &&
                  userRates.map((userRate, index) => {
                    let buttons = ['edit', 'delete'];
                    userRate.new && (buttons = ['submit', 'delete']);
                    userRate.edit && (buttons = ['submit', 'delete']);
                    return (
                      <StyledRowItem container key={index}>
                        <StyledRowCell
                          sx={{
                            minWidth: '150px',
                            p: userRate.new || userRate.edit ? 0 : '10px',
                          }}
                        >
                          {userRate.new || userRate.edit ? (
                            <FormControl sx={{ width: '100%' }}>
                              <StyledSelectLabel id='select-label'>
                                {getLocalString('labels', 'select')}
                              </StyledSelectLabel>
                              <StyledRowSelect
                                native={false}
                                labelId='select-label'
                                label={getLocalString('labels', 'select')}
                                onChange={event => selectHandler(event, index)}
                                variant='standard'
                                sx={{ width: '100%', border: 'none' }}
                                value={
                                  userRates[index].xlanceLiteralsId
                                    ? userRates[index].xlanceLiteralsId
                                    : null
                                }
                                MenuProps={{
                                  slots: { paper: StyledPaperDropdown },
                                }}
                                IconComponent={props => (
                                  <ChevronIcon direction={'down'} {...props} />
                                )}
                                renderValue={value => {
                                  return types.find(type => type.id === value)
                                    .name;
                                }}
                              >
                                {types
                                  .filter(item => item.active)
                                  .map((item, index) => (
                                    <MenuItem key={index} value={item.id}>
                                      {item.name}
                                    </MenuItem>
                                  ))}
                              </StyledRowSelect>
                            </FormControl>
                          ) : (
                            <Box>
                              {userRate.xlanceLiteralsId
                                ? types.find(
                                    type =>
                                      type.id === userRate.xlanceLiteralsId,
                                  ).name
                                : ''}
                            </Box>
                          )}
                        </StyledRowCell>
                        <StyledRowCell
                          sx={{ minWidth: '150px', textAlign: 'right' }}
                        >
                          {userRate.new || userRate.edit ? (
                            <StyledRowInput
                              //autoFocus
                              key={index}
                              placeholder={getLocalString('labels', 'input')}
                              variant='standard'
                              value={userRates[index].rate}
                              onChange={event => handleTextField(event, index)}
                            ></StyledRowInput>
                          ) : (
                            userRate.rate
                          )}
                        </StyledRowCell>
                        <StyledRowCell
                          sx={{
                            p: 0,
                            flexShrink: 0,
                            flexBasis: '226px',
                          }}
                        >
                          <Grid container>
                            <Grid sx={{ padding: '10px', flex: 1 }}>
                              {userRate.new || userRate.edit ? (
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                  adapterLocale={language}
                                >
                                  <StyledDatePicker
                                    format={'DD.MM.YYYY'}
                                    value={
                                      userRates[index].dateFrom
                                        ? dayjs(userRates[index].dateFrom)
                                        : null
                                    }
                                    //autoFocus={true}
                                    onChange={value => {
                                      if (value && value.isValid()) {
                                        dateHandler(value, index);
                                      }
                                    }}
                                    slots={{
                                      leftArrowIcon: () => (
                                        <ChevronIcon
                                          direction='left'
                                          viewBox='0 0 24 24'
                                          width={24}
                                          height={24}
                                        />
                                      ),
                                      rightArrowIcon: () => (
                                        <ChevronIcon
                                          direction='right'
                                          viewBox='0 0 24 24'
                                          width={24}
                                          height={24}
                                        />
                                      ),
                                      rightArrowButton: componentProps => (
                                        <IconButton
                                          disableRipple
                                          {...componentProps}
                                        >
                                          {componentProps.children}
                                        </IconButton>
                                      ),
                                      leftArrowButton: componentProps => (
                                        <IconButton
                                          disableRipple
                                          {...componentProps}
                                        >
                                          {componentProps.children}
                                        </IconButton>
                                      ),
                                      //textField: params => <TextField {...params} error={false} />,
                                    }}
                                  />
                                </LocalizationProvider>
                              ) : (
                                dayjs(userRate.dateFrom).format('DD.MM.YYYY')
                              )}
                            </Grid>
                            <Grid>
                              {buttons.map((action, indx) => (
                                <RowButton
                                  key={indx}
                                  action={action}
                                  rowIndex={index}
                                ></RowButton>
                              ))}
                            </Grid>
                          </Grid>
                        </StyledRowCell>
                      </StyledRowItem>
                    );
                  })}
                <Box
                  sx={{
                    p: '8px',
                    borderTop: '1px solid #E4E4EF',
                  }}
                >
                  <IconButton onClick={addObjectHandler} p={0}>
                    <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
                  </IconButton>
                </Box>
              </StyledBlockWrapper>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default UserPageRatesTab;
